import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  computeNextStored,
  extractMarketingParams,
  mergeStoredIntoParams,
  readStoredMarketingParams,
  shallowEqualParams,
  writeStoredMarketingParams
} from './marketingParams';

// BrowserRouter writes each location's key into window.history.state as it navigates, before React
// renders that location. A different key there means a newer navigation has already moved past the
// entry this render describes. A router that keeps its history in memory writes no key, so it is
// never stale.
const isSupersededByNewerNavigation = (renderedKey: string): boolean => {
  const writtenKey: unknown = window.history.state?.key;

  return typeof writtenKey === 'string' && writtenKey !== renderedKey;
};

export const useMarketingParams = (): void => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const incoming = extractMarketingParams(searchParams);
    const stored = readStoredMarketingParams();
    const next = computeNextStored(stored, incoming);
    if (!shallowEqualParams(stored, next)) writeStoredMarketingParams(next);

    const merged = mergeStoredIntoParams(searchParams, next);
    // A child that navigated in this same commit has already moved past the entry this render
    // describes: replacing the newer entry with this render's address would undo that navigation.
    // The effect runs again for the newer location.
    if (!merged || isSupersededByNewerNavigation(location.key)) return;

    // Keep the entry's history state: the Messages page holds its open AI chat there, and a replace
    // without it closes the chat (hooks/useSelectedAiThreadId).
    setSearchParams(merged, { replace: true, state: location.state });
  }, [location, searchParams, setSearchParams]);
};
